<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { PUBLIC_VERSION } from '$env/static/public';
	import { getLanguageFromPath, t } from '$lib/stores/i18n';
	import { onMount } from 'svelte';

	const appVersion = (PUBLIC_VERSION ?? '').trim();

	export let isOpen = false;
	export let currentUser: any = null;
	export let onClose: () => void;
	export let onToggle: () => void;
	export let onLogout: () => void;
	export let profileClassName: string = '';

	// Get current language from path
	$: lang = getLanguageFromPath($page.url.pathname) || 'en';

	// Close dropdown when clicking outside
	function handleClickOutside(event: MouseEvent) {
		const target = event.target as Element;
		if (isOpen && !target?.closest('.profile-dropdown')) {
			onClose();
		}
	}

	onMount(() => {
		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});

	function handleDashboardRoute(type: string) {
		goto(`/${lang}/3dprintingservice/dashboard?type=${type}`);
		onClose();
	}
</script>

<div class="profile-dropdown">
	<button class="profile-btn" aria-label={$t('profile.ariaLabels.profile')} on:click={onToggle}>
		{#if currentUser?.avatar}
			<img src={currentUser.avatar} alt={$t('profile.ariaLabels.profile')} class="profile-avatar" />
		{:else}
			<div class="profile-avatar-placeholder">
				{currentUser?.name?.charAt(0).toUpperCase() || 'U'}
			</div>
		{/if}
		<span class="profile-name">{currentUser?.name || $t('profile.user')}</span>
		<i class="fas fa-chevron-down"></i>
	</button>

	{#if isOpen}
		<div class="profile-menu {profileClassName}">
			<div class="profile-header">
				<div class="profile-info">
					<h4>{currentUser?.name || $t('profile.user')}</h4>
					<p>{currentUser?.email || 'user@example.com'}</p>
				</div>
			</div>
			<div class="profile-actions">
				{#if currentUser?.is_admin}
					<button class="profile-action-btn" on:click={() => handleDashboardRoute('quotation')}>
						<i class="fas fa-file-invoice"></i>
						<span>{$t('profile.admin.quotation')}</span>
					</button>
					<button class="profile-action-btn" on:click={() => handleDashboardRoute('invoice')}>
						<i class="fas fa-receipt"></i>
						<span>{$t('profile.admin.invoice')}</span>
					</button>
					<button class="profile-action-btn" on:click={() => handleDashboardRoute('model')}>
						<i class="fas fa-cube"></i>
						<span>{$t('profile.admin.model')}</span>
					</button>
					<button
						class="profile-action-btn"
						on:click={() => handleDashboardRoute('manage_origin_model')}
					>
						<i class="fas fa-database"></i>
						<span>{$t('profile.admin.originModel')}</span>
					</button>
					<button class="profile-action-btn" on:click={() => handleDashboardRoute('usermanage')}>
						<i class="fas fa-users-cog"></i>
						<span>{$t('profile.admin.userManage')}</span>
					</button>
					<button class="profile-action-btn" on:click={() => handleDashboardRoute('coupon')}>
						<i class="fas fa-ticket-alt"></i>
						<span>{$t('profile.admin.coupon')}</span>
					</button>
				{:else}
					<button
						class="profile-action-btn"
						on:click={() => handleDashboardRoute('purchase_history')}
					>
						<i class="fas fa-history"></i>
						<span>{$t('profile.purchaseHistory')}</span>
					</button>
				{/if}
				<hr class="profile-divider" />
				<a href="/{lang}/profile" rel="external" class="profile-action-btn">
					<i class="fas fa-user-edit"></i>
					<span>{$t('profile.editProfile')}</span>
				</a>
				<a href="/{lang}/change-password" rel="external" class="profile-action-btn">
					<i class="fas fa-key"></i>
					<span>{$t('profile.changePassword')}</span>
				</a>
				<hr class="profile-divider" />
				<button class="profile-action-btn logout-btn" on:click={onLogout}>
					<i class="fas fa-sign-out-alt"></i>
					<span>{$t('profile.logout')}</span>
				</button>
				{#if appVersion}
					<p class="profile-version" aria-live="polite">Version: {appVersion}</p>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style scoped>
	/* Profile Styles */
	.profile-dropdown {
		position: relative;
	}

	.profile-btn {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		background: none;
		border: none;
		color: #fff;
		cursor: pointer;
		transition: all 0.3s ease;
		border-radius: 8px;
	}

	.profile-btn:hover {
		background: rgba(255, 255, 255, 0.1);
	}

	.profile-avatar {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		object-fit: cover;
	}

	.profile-avatar-placeholder {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background: #4a6cf7;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		font-weight: 600;
		font-size: 14px;
	}

	.profile-name {
		font-size: 14px;
		font-weight: 500;
		max-width: 120px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.profile-menu {
		position: absolute;
		top: 100%;
		right: 0;
		background: #060606;
		border-radius: 12px;
		box-shadow: 0px 0px 9px 3px rgba(255, 255, 255, 0.3);
		min-width: 280px;
		z-index: 1000;
		overflow: hidden;
		margin-top: 8px;
	}

	.profile-header {
		padding: 20px;
		background: linear-gradient(135deg, #4a6cf7 0%, #3a5ae7 100%);
		color: #fff;
	}

	.profile-info h4 {
		margin: 0 0 4px 0;
		font-size: 16px;
		font-weight: 600;
	}

	.profile-info p {
		margin: 0;
		font-size: 14px;
		opacity: 0.9;
	}

	.profile-actions {
		padding: 12px 0;
	}

	.profile-action-btn {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		padding: 12px 20px;
		background: none;
		border: none;
		color: #fff;
		cursor: pointer;
		transition: background-color 0.2s ease;
		font-size: 14px;
	}

	.profile-action-btn:hover {
		background: rgba(255, 255, 255, 0.05);
	}

	.profile-action-btn.logout-btn {
		color: #ff4444;
	}

	.profile-action-btn.logout-btn:hover {
		background: rgba(255, 68, 68, 0.1);
	}

	.profile-action-btn i {
		width: 16px;
		text-align: center;
	}

	.profile-divider {
		margin: 8px 0;
		border: none;
		border-top: 1px solid #333;
	}

	.profile-version {
		margin: 0;
		padding: 10px 20px 14px;
		font-size: 12px;
		color: rgba(255, 255, 255, 0.45);
		text-align: center;
		border-top: 1px solid #333;
	}
	.mobile-profile-dropdown {
		position: absolute;
		top: 0px;
		left: 0;
		background: #1a1a1a;
		border-radius: 0px;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
		z-index: 1000;
		min-width: 260px;
	}
	/* Mobile responsive */
	@media (max-width: 768px) {
		.profile-name {
			display: none;
		}

		.profile-menu {
			min-width: 250px;
		}
	}
</style>
